import React, { useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { HomeMode, NotificationsMode, TasksMode, StatusSummaryMode } from './modes';
import { NotificationRecord } from '../../organisms/NotificationFeed/useNotificationFeed';
import { Task } from '../TaskList/TaskListMolecule.types';
import { useArmillaryContextOrNull } from '../../../context/ArmillaryContext';
import { useToolbarContextOrNull } from '../../../context/ToolbarContext';
import './ArmillaryActionsMolecule.scss';

export type PanelMode = 'home' | 'notifications' | 'tasks' | 'status';

export interface ArmillaryActionsMoleculeProps {
  /** Root sphere ID (the sphere the Armillary loaded with) */
  rootSphereId: string;
  /** Currently active sphere ID (root or selected orbital) */
  activeSphereId: string;
  /** Label for the active sphere (for context indicator) */
  activeSphereLabel?: string;
  /** Whether an orbital is currently selected (shows context indicator) */
  isOrbitalSelected?: boolean;
  /** Callback to clear orbital selection (close context) */
  onClearOrbitalSelection?: () => void;
  /** Current panel mode */
  mode?: PanelMode;
  /** Callback when mode changes */
  onModeChange?: (mode: PanelMode) => void;
  /** Callback when a notification is clicked */
  onNotificationClick?: (notification: NotificationRecord) => void;
  /** Callback when a task is clicked */
  onTaskClick?: (task: Task) => void;
  /** Callback when check-in action is triggered */
  onCheckIn?: (task: Task) => void;
  /** Whether to hide the panel (e.g., when left toolbar is open) */
  isHidden?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ArmillaryActionsMolecule - Context-aware notifications panel for the Armillary view
 *
 * Features:
 * - Mode switching (notifications, messages, status)
 * - Dynamic context based on orbital selection
 * - Card stack with navigation and view modes
 * - Integration with notification service
 */
const ArmillaryActionsMolecule: React.FC<ArmillaryActionsMoleculeProps> = ({
  rootSphereId: _rootSphereId,
  activeSphereId,
  activeSphereLabel,
  isOrbitalSelected = false,
  onClearOrbitalSelection,
  mode: controlledMode,
  onModeChange,
  onNotificationClick,
  onTaskClick,
  onCheckIn,
  isHidden = false,
  className = '',
}) => {
  const { t } = useTranslation();

  // Get orbital data from ArmillaryContext, notification data from ToolbarContext
  const armillaryContext = useArmillaryContextOrNull();
  const toolbarContext = useToolbarContextOrNull();
  const orbitalSphereIds = armillaryContext?.orbitalSphereIds ?? [];
  const unreadCount = toolbarContext?.notificationState.unreadCount ?? 0;

  // Internal mode state (for uncontrolled usage)
  const [internalMode, setInternalMode] = useState<PanelMode>('home');

  // Use controlled or uncontrolled mode
  const currentMode = controlledMode ?? internalMode;
  const handleModeChange = useCallback(
    (newMode: PanelMode) => {
      if (onModeChange) {
        onModeChange(newMode);
      } else {
        setInternalMode(newMode);
      }
    },
    [onModeChange]
  );

  // Mode icons and labels
  const modes: Array<{ mode: PanelMode; icon: string; labelKey: string }> = [
    { mode: 'home', icon: 'pi-home', labelKey: 'armillaryActions.header.home' },
    { mode: 'notifications', icon: 'pi-bell', labelKey: 'armillaryActions.header.notifications' },
    { mode: 'tasks', icon: 'pi-check-square', labelKey: 'armillaryActions.header.tasks' },
    { mode: 'status', icon: 'pi-chart-bar', labelKey: 'armillaryActions.header.status' },
  ];

  // Get current mode label
  const currentModeConfig = modes.find((m) => m.mode === currentMode);
  const currentModeLabel = currentModeConfig ? t(currentModeConfig.labelKey) : '';

  return (
    <div className={`armillary-actions-molecule ${isHidden ? 'armillary-actions-molecule--hidden' : ''} ${className}`}>
      {/* Header with mode title and mode switcher */}
      <div className="armillary-actions-molecule__header">
        <div className="armillary-actions-molecule__header-left">
          <i className={`pi ${currentModeConfig?.icon || 'pi-bell'}`} />
          <h3 className="armillary-actions-molecule__title">{currentModeLabel}</h3>
          {currentMode === 'notifications' && unreadCount > 0 && (
            <span className="armillary-actions-molecule__badge">{unreadCount}</span>
          )}
        </div>
        <div className="armillary-actions-molecule__mode-switcher">
          {modes.map(({ mode, icon, labelKey }) => (
            <Button
              key={mode}
              icon={`pi ${icon}`}
              className={`armillary-actions-molecule__mode-btn ${currentMode === mode ? 'armillary-actions-molecule__mode-btn--active' : ''}`}
              onClick={() => handleModeChange(mode)}
              aria-label={t(labelKey)}
              text
              rounded
            />
          ))}
        </div>
      </div>

      {/* Context indicator (when orbital is selected) */}
      {isOrbitalSelected && activeSphereLabel && (
        <div className="armillary-actions-molecule__context">
          <i className="pi pi-map-marker" />
          <span className="armillary-actions-molecule__context-label">
            {activeSphereLabel}
          </span>
          {onClearOrbitalSelection && (
            <button
              className="armillary-actions-molecule__context-close"
              onClick={onClearOrbitalSelection}
              aria-label={t('armillaryActions.context.clearSelection')}
              type="button"
            >
              <i className="pi pi-times" />
            </button>
          )}
        </div>
      )}

      {/* Mode content */}
      <div className="armillary-actions-molecule__content">
        {currentMode === 'home' && (
          <HomeMode
            activeSphereId={activeSphereId}
            additionalSphereIds={orbitalSphereIds}
            onNotificationClick={onNotificationClick}
            onTaskClick={onTaskClick}
            onCheckIn={onCheckIn}
          />
        )}
        {currentMode === 'notifications' && (
          <NotificationsMode
            activeSphereId={activeSphereId}
            additionalSphereIds={orbitalSphereIds}
          />
        )}
        {currentMode === 'tasks' && (
          <TasksMode
            activeSphereId={activeSphereId}
            additionalSphereIds={orbitalSphereIds}
            onTaskClick={onTaskClick}
            onCheckIn={onCheckIn}
          />
        )}
        {currentMode === 'status' && (
          <StatusSummaryMode activeSphereId={activeSphereId} />
        )}
      </div>
    </div>
  );
};

export default ArmillaryActionsMolecule;
